import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { create } from '@bufbuild/protobuf';
import {
  App,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Plus, Trash2 } from 'lucide-react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { channelApi } from '@/api/channel.api';
import { applicationApi } from '@/api/application.api';
import { agentApi } from '@/api/agent.api';
import { ApiError } from '@/api/errors';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { ListRequestSchema as HarnessListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  ChannelAccountBindingMode,
  ChannelAccountCredentialSchema,
  ChannelType,
  ChatPolicySchema,
} from '@/proto/agentplatform/v1/channel_pb';
import {
  BridgeAgentTokenInitSchema,
  CheckChannelAccountNameRequestSchema,
  CreateChannelAccountRequestSchema,
  ListChannelAccountsRequestSchema,
  UpdateChannelAccountRequestSchema,
} from '@/proto/agentplatform/httpv2/channel_pb';
import { localDateToExpireNano } from '@/utils/utils';
import {
  CHANNEL_TYPES,
  channelCapabilitiesImplyGroupChat,
  channelTypeConventionallySupportsGroup,
  credentialSecretComparable,
  formatIdList,
  parseIdList,
} from './helpers';
import DsIcon from '@/components/Layout/DsIcon';

export type AccountDrawerMode = 'create' | 'edit';

/** 签发成功后一次性明文 token（供页面弹 reveal 使用）。 */
export interface RevealedToken {
  name: string;
  token: string;
}

interface ChannelAccountFormDrawerProps {
  open: boolean;
  mode: AccountDrawerMode;
  editAccountId: string | null;
  /** 默认渠道类型（新建时由当前选中的类型带入） */
  defaultType?: ChannelType;
  onClose: () => void;
  onSaved: () => void;
  /** WebSocket 创建并同步签发 token 成功后回调，页面据此弹一次性明文。 */
  onTokensRevealed?: (tokens: RevealedToken[]) => void;
}

type CredentialBaseline = { appId: string; appSecret: string };

type TokenExpireMode = 'forever' | 'custom';
interface TokenDraft {
  name: string;
  expireMode: TokenExpireMode;
  expireDate?: Dayjs;
}

const APPLICATION_PICKER_PAGE_SIZE = 20;
/** 拉取已绑定 BRIDGE 账号时的分页大小（用于计算下拉置灰集合）。 */
const BRIDGE_BOUND_PAGE_SIZE = 200;

function makeTokenDraft(name = 'default-token'): TokenDraft {
  return { name, expireMode: 'forever', expireDate: undefined };
}

/** 创建/编辑渠道账号抽屉。飞书/QQ 业务逻辑（凭证掩码比对、聊天策略、绑定模式）逐行保留，
 *  WebSocket 分支按参考页新增（绑定对象 + 多 Token 同步签发 + 一次性明文）。 */
export default function ChannelAccountFormDrawer({
  open,
  mode,
  editAccountId,
  defaultType = ChannelType.FEISHU,
  onClose,
  onSaved,
  onTokensRevealed,
}: ChannelAccountFormDrawerProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [formName, setFormName] = useState('');
  /** 名称唯一性校验错误（同租户同渠道类型下需唯一），空串表示无错误。 */
  const [nameError, setNameError] = useState('');
  const [formType, setFormType] = useState<ChannelType>(defaultType);
  /** 绑定到的智能体实例，即 applications.id，对应 ChannelAccount.agentId */
  const [formApplicationId, setFormApplicationId] = useState('');
  const [formAppId, setFormAppId] = useState('');
  const [formAppSecret, setFormAppSecret] = useState('');
  const [formP2pAllowFrom, setFormP2pAllowFrom] = useState('');
  const [formDisableP2p, setFormDisableP2p] = useState(false);
  const [formDisableGroup, setFormDisableGroup] = useState(false);
  const [formGroupAllowFrom, setFormGroupAllowFrom] = useState('');
  const [formShowToolCalls, setFormShowToolCalls] = useState(true);
  const [formApprovers, setFormApprovers] = useState('');
  /** 可自我审批人员，命中后其发起的审批可自行通过（chat_policy.self_approvers）。 */
  const [formSelfApprovers, setFormSelfApprovers] = useState('');
  const [formUnknownCommandAsAgentInput, setFormUnknownCommandAsAgentInput] = useState(false);
  /** 调用 VibeAgent 时 _caller_user_id 使用渠道用户的哪个字段，空串 = channel_user_id（默认）。 */
  const [formCallerUserIdField, setFormCallerUserIdField] = useState('');
  const [formBindingMode, setFormBindingMode] = useState<ChannelAccountBindingMode>(
    ChannelAccountBindingMode.INSTANCE
  );

  // —— WebSocket 专属：同步创建 Token 草稿 —— //
  const [formCreateToken, setFormCreateToken] = useState(true);
  const [formTokens, setFormTokens] = useState<TokenDraft[]>([makeTokenDraft()]);

  const isWebSocket = formType === ChannelType.BRIDGE;

  /** 编辑页加载成功后的凭证快照，用于判断 PUT 是否携带 credential */
  const editCredentialBaselineRef = useRef<CredentialBaseline | null>(null);

  const resetAccountForm = (type: ChannelType = defaultType) => {
    editCredentialBaselineRef.current = null;
    setFormName('');
    setNameError('');
    setFormType(type);
    setFormApplicationId('');
    setFormAppId('');
    setFormAppSecret('');
    setFormP2pAllowFrom('');
    setFormDisableP2p(false);
    setFormDisableGroup(false);
    setFormGroupAllowFrom('');
    setFormShowToolCalls(true);
    setFormApprovers('');
    setFormSelfApprovers('');
    setFormUnknownCommandAsAgentInput(false);
    setFormCallerUserIdField('');
    setFormBindingMode(ChannelAccountBindingMode.INSTANCE);
    setFormCreateToken(type === ChannelType.BRIDGE);
    setFormTokens([makeTokenDraft()]);
  };

  // 新建打开时用默认类型初始化（编辑时由详情覆盖）。
  useEffect(() => {
    if (open && mode === 'create') {
      resetAccountForm(defaultType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const {
    data: applicationsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: applicationsLoading,
  } = useInfiniteQuery({
    queryKey: ['applications', 'channel_account_create_infinite'] as const,
    initialPageParam: 1,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      applicationApi.list(
        create(ListRequestSchema, {
          page: pageParam,
          pageSize: APPLICATION_PICKER_PAGE_SIZE,
        })
      ),
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.total;
      const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      if (loaded >= total) {
        return undefined;
      }
      return allPages.length + 1;
    },
    enabled: open,
  });

  const { data: channelsForCapsResponse } = useQuery({
    queryKey: ['channels', 'account_drawer_caps'] as const,
    queryFn: () =>
      channelApi.listChannels(create(HarnessListRequestSchema, { page: 1, pageSize: 200 })),
    enabled: open,
  });

  const { data: editAccountDetail, isLoading: editDetailLoading } = useQuery({
    queryKey: ['channel_account', editAccountId] as const,
    queryFn: () => channelApi.getChannelAccount(editAccountId!),
    enabled: open && mode === 'edit' && !!editAccountId,
  });

  useEffect(() => {
    if (!open || mode !== 'edit' || !editAccountDetail) {
      return;
    }
    const a = editAccountDetail;
    setFormName(a.name ?? '');
    setFormType(a.type);
    setFormApplicationId(a.agentId ?? '');
    setFormAppId(a.credential?.appId ?? '');
    setFormAppSecret(a.credential?.appSecret ?? '');
    const cp = a.chatPolicy;
    setFormP2pAllowFrom(formatIdList(cp?.p2pAllowFrom ?? []));
    setFormDisableP2p(cp?.disableP2p ?? false);
    setFormDisableGroup(cp?.disableGroup ?? false);
    setFormGroupAllowFrom(formatIdList(cp?.groupAllowFrom ?? []));
    setFormShowToolCalls(cp?.showToolCalls ?? true);
    setFormApprovers(formatIdList(cp?.approvers ?? []));
    setFormSelfApprovers(formatIdList(cp?.selfApprovers ?? []));
    setFormUnknownCommandAsAgentInput(cp?.unknownCommandAsAgentInput ?? false);
    setFormCallerUserIdField(cp?.callerUserIdField ?? '');
    setFormBindingMode(a.bindingMode ?? ChannelAccountBindingMode.INSTANCE);
    editCredentialBaselineRef.current = {
      appId: a.credential?.appId ?? '',
      appSecret: a.credential?.appSecret ?? '',
    };
  }, [open, mode, editAccountDetail]);

  const channelSupportsGroup = useMemo(() => {
    const row = channelsForCapsResponse?.data?.find((ch) => ch.type === formType);
    const inferred = channelCapabilitiesImplyGroupChat(row?.capabilities);
    if (inferred !== null) {
      return inferred;
    }
    return channelTypeConventionallySupportsGroup(formType);
  }, [channelsForCapsResponse?.data, formType]);

  useEffect(() => {
    if (!channelSupportsGroup) {
      setFormDisableGroup(false);
      setFormGroupAllowFrom('');
      setFormShowToolCalls(true);
      setFormApprovers('');
      setFormSelfApprovers('');
    }
  }, [channelSupportsGroup, editAccountDetail]);

  const applications = useMemo(
    () => applicationsPages?.pages.flatMap((p) => p.data).filter(Boolean) ?? [],
    [applicationsPages?.pages]
  );

  const { data: agentsResponse } = useQuery({
    queryKey: ['agents', 'list', 'for-channel-picker'] as const,
    queryFn: () => agentApi.listAgents(create(ListRequestSchema, { page: 1, pageSize: 200 })),
    enabled: open && formBindingMode === ChannelAccountBindingMode.TEMPLATE,
  });
  const agents = useMemo(() => agentsResponse?.data?.filter(Boolean) ?? [], [agentsResponse?.data]);

  // WebSocket(BRIDGE) 一个 agent 只能绑定一个渠道账号：拉取全部 BRIDGE 账号，得到已绑定的 agentId 集合，
  // 用于在绑定对象下拉中置灰这些 agent。BRIDGE 账号数量受「每个 agent 至多绑一个」约束，通常很小，
  // 循环拉全即可。后端校验（websocket_agent_bound 409）仍是最终保证，此处仅为体验优化。
  const { data: boundAgentIds } = useQuery({
    queryKey: ['channel_accounts', 'bound_agent_ids', ChannelType.BRIDGE] as const,
    queryFn: async () => {
      const ids = new Set<string>();
      let page = 1;
      for (;;) {
        const data = await channelApi.listChannelAccounts(
          create(ListChannelAccountsRequestSchema, {
            page,
            pageSize: BRIDGE_BOUND_PAGE_SIZE,
            type: ChannelType.BRIDGE,
          })
        );
        for (const acc of data.items) {
          if (acc.agentId) {
            ids.add(acc.agentId);
          }
        }
        if (
          data.items.length < BRIDGE_BOUND_PAGE_SIZE ||
          page * BRIDGE_BOUND_PAGE_SIZE >= data.total
        ) {
          break;
        }
        page += 1;
      }
      return ids;
    },
    enabled: open && isWebSocket,
  });

  const applicationPickerScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 56;
    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  /** WebSocket 绑定对象下拉滚动到底继续加载（INSTANCE 走无限列表）。 */
  const boundSelectScroll = (e: UIEvent<HTMLDivElement>) => {
    if (formBindingMode !== ChannelAccountBindingMode.INSTANCE) return;
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 56;
    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  /** WebSocket 绑定对象候选（按绑定模式取实例或模板）。已绑定其它 BRIDGE 账号的 agent 置灰。 */
  const boundOptions = useMemo(() => {
    const taken = boundAgentIds ?? new Set<string>();
    // 编辑态排除账号自身当前绑定的 agent，避免把它错误置灰。
    const selfAgentId = mode === 'edit' ? (editAccountDetail?.agentId ?? '') : '';
    const build = (id: string, name?: string) => {
      const base = name?.trim() ? `${name}（${id}）` : `${id}`;
      const disabled = id !== selfAgentId && taken.has(id);
      return {
        value: id,
        label: disabled ? `${base}（已绑定 websocket 账号）` : base,
        disabled,
      };
    };
    if (formBindingMode === ChannelAccountBindingMode.TEMPLATE) {
      return agents.map((ag) => build(ag.id, ag.name));
    }
    return applications.map((app) => build(app.id, app.name));
  }, [formBindingMode, agents, applications, boundAgentIds, mode, editAccountDetail]);

  const credentialValid = formAppId.trim().length > 0 && formAppSecret.trim().length > 0;

  const chatPolicyFields = () =>
    create(ChatPolicySchema, {
      p2pAllowFrom: parseIdList(formP2pAllowFrom),
      disableP2p: formDisableP2p,
      disableGroup: channelSupportsGroup ? formDisableGroup : false,
      groupAllowFrom: channelSupportsGroup ? parseIdList(formGroupAllowFrom) : [],
      showToolCalls: channelSupportsGroup ? formShowToolCalls : true,
      approvers: channelSupportsGroup ? parseIdList(formApprovers) : [],
      selfApprovers: channelSupportsGroup ? parseIdList(formSelfApprovers) : [],
      unknownCommandAsAgentInput: formUnknownCommandAsAgentInput,
      callerUserIdField: formCallerUserIdField,
    });

  // —— Token 草稿操作 —— //
  const addTokenDraft = () =>
    setFormTokens((ts) => [...ts, makeTokenDraft(`token-${ts.length + 1}`)]);
  const removeTokenDraft = (i: number) => setFormTokens((ts) => ts.filter((_, idx) => idx !== i));
  const patchTokenDraft = (i: number, patch: Partial<TokenDraft>) =>
    setFormTokens((ts) => ts.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  const createMutation = useMutation({
    mutationFn: () => {
      const req = create(CreateChannelAccountRequestSchema, {
        name: formName.trim(),
        type: formType,
        agentId: formApplicationId,
        bindingMode: formBindingMode,
        credential: create(ChannelAccountCredentialSchema, {
          appId: formAppId.trim(),
          appSecret: formAppSecret.trim(),
        }),
        chatPolicy: chatPolicyFields(),
      });
      return channelApi.createChannelAccount(req);
    },
    onSuccess: () => {
      const savedType = formType;
      resetAccountForm();
      queryClient.invalidateQueries({ queryKey: ['channel_accounts', savedType] });
      queryClient.invalidateQueries({ queryKey: ['channel_accounts', 'count', savedType] });
      message.success('渠道账号已创建');
      onSaved();
    },
    onError: (e) => {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    },
  });

  /** WebSocket 创建：单请求原子创建渠道账号 + WEBSOCKET Token，返回一次性明文列表。 */
  const createWebSocketMutation = useMutation({
    mutationFn: async () => {
      const bridgeTokens = formCreateToken
        ? formTokens.map((draft) =>
            create(BridgeAgentTokenInitSchema, {
              name: draft.name.trim(),
              expireAt:
                draft.expireMode === 'custom' && draft.expireDate
                  ? localDateToExpireNano(draft.expireDate.format('YYYY-MM-DD'))
                  : 0n,
            })
          )
        : [];

      const { bridgeTokens: created } = await channelApi.createChannelAccount(
        create(CreateChannelAccountRequestSchema, {
          name: formName.trim(),
          type: ChannelType.BRIDGE,
          agentId: formApplicationId,
          bindingMode: formBindingMode,
          bridgeTokens,
        })
      );

      return created.map<RevealedToken>((t) => ({ name: t.name, token: t.token }));
    },
    onSuccess: (revealed) => {
      resetAccountForm();
      queryClient.invalidateQueries({ queryKey: ['channel_accounts', ChannelType.BRIDGE] });
      queryClient.invalidateQueries({
        queryKey: ['channel_accounts', 'count', ChannelType.BRIDGE],
      });
      queryClient.invalidateQueries({
        queryKey: ['channel_accounts', 'bound_agent_ids', ChannelType.BRIDGE],
      });
      queryClient.invalidateQueries({ queryKey: ['agent-tokens'] });
      if (revealed.length > 0) {
        onTokensRevealed?.(revealed);
      } else {
        message.success('WebSocket 渠道账号已创建');
      }
      onSaved();
    },
    onError: (e) => {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const id = editAccountId;
      if (!id || !editAccountDetail) {
        throw new Error('missing account');
      }
      const baseline = editCredentialBaselineRef.current;
      const curAppId = formAppId.trim();
      const curSecretEff = credentialSecretComparable(formAppSecret);
      const baseAppId = (baseline?.appId ?? '').trim();
      const baseSecretEff = credentialSecretComparable(baseline?.appSecret ?? '');

      const credentialModified =
        baseline != null && (curAppId !== baseAppId || curSecretEff !== baseSecretEff);
      const shouldSendCredential =
        credentialModified && curAppId.length > 0 && curSecretEff.length > 0;

      const req = create(UpdateChannelAccountRequestSchema, {
        id,
        name: formName.trim(),
        agentId: formApplicationId,
        bindingMode: formBindingMode,
        ...(shouldSendCredential
          ? {
              credential: create(ChannelAccountCredentialSchema, {
                appId: curAppId,
                appSecret: curSecretEff,
              }),
            }
          : {}),
        chatPolicy: chatPolicyFields(),
      });
      await channelApi.updateChannelAccount(req);
      return id;
    },
    onSuccess: (savedId) => {
      const savedType = formType;
      resetAccountForm();
      queryClient.invalidateQueries({ queryKey: ['channel_accounts', savedType] });
      queryClient.invalidateQueries({ queryKey: ['channel_accounts', 'count', savedType] });
      queryClient.removeQueries({ queryKey: ['channel_account', savedId] });
      message.success('渠道账号已保存');
      onSaved();
    },
    onError: (e) => {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    },
  });

  const handleClose = () => {
    resetAccountForm();
    onClose();
  };

  const submitting =
    createMutation.isPending || createWebSocketMutation.isPending || updateMutation.isPending;

  /** WebSocket 创建的必填校验（对齐参考页）。 */
  function submitWebSocketCreate() {
    if (!formName.trim()) {
      message.error('账号名称必填');
      return;
    }
    if (!formApplicationId) {
      message.error('请选择绑定 Agent');
      return;
    }
    if (formCreateToken) {
      const names = formTokens.map((t) => t.name.trim());
      if (names.some((n) => !n)) {
        message.error('Token 名称必填');
        return;
      }
      if (new Set(names).size !== names.length) {
        message.error('同一渠道账号下 Token 名称需唯一');
        return;
      }
    }
    createWebSocketMutation.mutate();
  }

  /** 非 WebSocket（飞书/QQ）创建/编辑校验，与原逻辑一致。 */
  const legacyDisabled =
    mode === 'create'
      ? !formName.trim() || !formApplicationId || !credentialValid
      : !formName.trim() || !formApplicationId || editDetailLoading || !editAccountDetail;

  /** 校验名称在「同租户 + 同渠道类型」范围内是否唯一。网络异常时放行（后端 409 兜底）。 */
  async function validateNameAvailable(): Promise<boolean> {
    const name = formName.trim();
    if (!name) {
      return true; // 必填校验在各提交分支中单独处理
    }
    try {
      const available = await channelApi.checkName(
        create(CheckChannelAccountNameRequestSchema, {
          name,
          type: formType,
          excludeId: mode === 'edit' ? (editAccountId ?? '') : '',
        })
      );
      if (!available) {
        setNameError('该名称已存在（同租户同渠道类型下需唯一）');
        return false;
      }
      setNameError('');
      return true;
    } catch {
      return true;
    }
  }

  function onSubmit() {
    void (async () => {
      if (!(await validateNameAvailable())) {
        return;
      }
      if (mode === 'create' && isWebSocket) {
        submitWebSocketCreate();
        return;
      }
      if (mode === 'create') {
        createMutation.mutate();
        return;
      }
      updateMutation.mutate();
    })();
  }

  const submitDisabled = isWebSocket && mode === 'create' ? false : legacyDisabled;

  const drawerTitle = (() => {
    const label = CHANNEL_TYPES.find((t) => t.type === formType)?.label ?? '';
    return mode === 'create' ? `新建${label}渠道账号` : '编辑渠道账号';
  })();

  return (
    <Drawer
      title={drawerTitle}
      open={open}
      onClose={handleClose}
      width={620}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleClose}>取消</Button>
          <Button type="primary" loading={submitting} disabled={submitDisabled} onClick={onSubmit}>
            {mode === 'create' ? '创建' : '保存'}
          </Button>
        </div>
      }
    >
      {mode === 'edit' && editDetailLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '48px 0' }}>
          <Spin />
          <span style={{ color: 'var(--text-color-placeholder)' }}>加载账号详情…</span>
        </div>
      ) : (
        <Form layout="vertical">
          <Form.Item label="渠道类型" required>
            <Radio.Group
              value={formType}
              disabled={mode === 'edit'}
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => {
                const next = e.target.value as ChannelType;
                setFormType(next);
                setFormApplicationId('');
                setFormCreateToken(next === ChannelType.BRIDGE);
              }}
            >
              {CHANNEL_TYPES.map((t) => (
                <Radio.Button key={t.type} value={t.type}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <DsIcon name={t.icon} size={14} />
                    {t.label}
                  </span>
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="账号名称"
            required
            validateStatus={nameError ? 'error' : undefined}
            help={nameError || undefined}
          >
            <Input
              value={formName}
              onChange={(e) => {
                setFormName(e.target.value);
                if (nameError) {
                  setNameError('');
                }
              }}
              onBlur={() => {
                void validateNameAvailable();
              }}
              placeholder={isWebSocket ? '例如 prod-h5-default' : '显示名称'}
            />
          </Form.Item>

          {isWebSocket ? (
            <WebSocketFields
              bindingMode={formBindingMode}
              onBindingModeChange={(m) => {
                setFormBindingMode(m);
                setFormApplicationId('');
              }}
              boundId={formApplicationId}
              onBoundIdChange={setFormApplicationId}
              boundOptions={boundOptions}
              onBoundSelectScroll={boundSelectScroll}
              createToken={formCreateToken}
              onCreateTokenChange={setFormCreateToken}
              tokens={formTokens}
              onAddToken={addTokenDraft}
              onRemoveToken={removeTokenDraft}
              onPatchToken={patchTokenDraft}
            />
          ) : (
            <LegacyChannelFields
              mode={mode}
              bindingMode={formBindingMode}
              onBindingModeChange={(m) => {
                setFormBindingMode(m);
                setFormApplicationId('');
              }}
              applicationId={formApplicationId}
              onApplicationIdChange={setFormApplicationId}
              applications={applications}
              applicationsLoading={applicationsLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onApplicationPickerScroll={applicationPickerScroll}
              agents={agents}
              appId={formAppId}
              onAppIdChange={setFormAppId}
              appSecret={formAppSecret}
              onAppSecretChange={setFormAppSecret}
              channelSupportsGroup={channelSupportsGroup}
              p2pAllowFrom={formP2pAllowFrom}
              onP2pAllowFromChange={setFormP2pAllowFrom}
              disableP2p={formDisableP2p}
              onDisableP2pChange={setFormDisableP2p}
              disableGroup={formDisableGroup}
              onDisableGroupChange={setFormDisableGroup}
              groupAllowFrom={formGroupAllowFrom}
              onGroupAllowFromChange={setFormGroupAllowFrom}
              showToolCalls={formShowToolCalls}
              onShowToolCallsChange={setFormShowToolCalls}
              approvers={formApprovers}
              onApproversChange={setFormApprovers}
              selfApprovers={formSelfApprovers}
              onSelfApproversChange={setFormSelfApprovers}
              unknownCommandAsAgentInput={formUnknownCommandAsAgentInput}
              onUnknownCommandAsAgentInputChange={setFormUnknownCommandAsAgentInput}
              callerUserIdField={formCallerUserIdField}
              onCallerUserIdFieldChange={setFormCallerUserIdField}
            />
          )}
        </Form>
      )}
    </Drawer>
  );
}

// ————————————————— WebSocket 子表单 ————————————————— //

interface WebSocketFieldsProps {
  bindingMode: ChannelAccountBindingMode;
  onBindingModeChange: (m: ChannelAccountBindingMode) => void;
  boundId: string;
  onBoundIdChange: (id: string) => void;
  boundOptions: { value: string; label: string; disabled?: boolean }[];
  onBoundSelectScroll: (e: UIEvent<HTMLDivElement>) => void;
  createToken: boolean;
  onCreateTokenChange: (v: boolean) => void;
  tokens: TokenDraft[];
  onAddToken: () => void;
  onRemoveToken: (i: number) => void;
  onPatchToken: (i: number, patch: Partial<TokenDraft>) => void;
}

function WebSocketFields({
  bindingMode,
  onBindingModeChange,
  boundId,
  onBoundIdChange,
  boundOptions,
  onBoundSelectScroll,
  createToken,
  onCreateTokenChange,
  tokens,
  onAddToken,
  onRemoveToken,
  onPatchToken,
}: WebSocketFieldsProps) {
  return (
    <>
      <Form.Item label="绑定模式" required>
        <Radio.Group
          value={bindingMode}
          optionType="button"
          buttonStyle="solid"
          onChange={(e) => onBindingModeChange(e.target.value as ChannelAccountBindingMode)}
        >
          <Radio.Button value={ChannelAccountBindingMode.TEMPLATE}>绑定 Agent 模板</Radio.Button>
          <Radio.Button value={ChannelAccountBindingMode.INSTANCE}>
            绑定 Agent 实例（默认）
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label="绑定对象"
        required
        extra={`选择要绑定的 Agent ${
          bindingMode === ChannelAccountBindingMode.TEMPLATE ? '模板' : '实例'
        }`}
      >
        <Select
          value={boundId || undefined}
          placeholder="请选择"
          options={boundOptions}
          onChange={onBoundIdChange}
          onPopupScroll={onBoundSelectScroll}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>

      <Form.Item>
        <Checkbox checked={createToken} onChange={(e) => onCreateTokenChange(e.target.checked)}>
          同步创建 Token（可一次创建多个）
        </Checkbox>
      </Form.Item>

      {createToken && (
        <>
          {tokens.map((tk, i) => (
            <div
              key={i}
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-3)',
                background: 'var(--fill-color-lighter)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--space-2)',
                }}
              >
                <Typography.Text strong style={{ fontSize: 'var(--fs-secondary)' }}>
                  Token {i + 1}
                </Typography.Text>
                {tokens.length > 1 && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<Trash2 size={13} />}
                    onClick={() => onRemoveToken(i)}
                  >
                    移除
                  </Button>
                )}
              </div>
              <Form.Item label="Token 名称" style={{ marginBottom: 'var(--space-2)' }} required>
                <Input
                  value={tk.name}
                  onChange={(e) => onPatchToken(i, { name: e.target.value })}
                  placeholder="单渠道账号下唯一，例如 default-token"
                />
              </Form.Item>
              <Form.Item label="有效期" style={{ marginBottom: 0 }}>
                <Space wrap>
                  <Radio.Group
                    value={tk.expireMode}
                    onChange={(e) =>
                      onPatchToken(i, { expireMode: e.target.value as TokenExpireMode })
                    }
                  >
                    <Radio value="forever">永久有效</Radio>
                    <Radio value="custom">自定义</Radio>
                  </Radio.Group>
                  {tk.expireMode === 'custom' && (
                    <DatePicker
                      value={tk.expireDate}
                      onChange={(d) => onPatchToken(i, { expireDate: d ?? undefined })}
                      placeholder="选择到期日"
                      disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                    />
                  )}
                </Space>
              </Form.Item>
            </div>
          ))}
          <Button type="link" icon={<Plus size={14} />} onClick={onAddToken} style={{ padding: 0 }}>
            添加 Token
          </Button>
        </>
      )}
    </>
  );
}

// ————————————————— 飞书 / QQ 子表单（原逻辑视图） ————————————————— //

interface LegacyChannelFieldsProps {
  mode: AccountDrawerMode;
  bindingMode: ChannelAccountBindingMode;
  onBindingModeChange: (m: ChannelAccountBindingMode) => void;
  applicationId: string;
  onApplicationIdChange: (id: string) => void;
  applications: { id: string; name: string }[];
  applicationsLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onApplicationPickerScroll: (e: UIEvent<HTMLDivElement>) => void;
  agents: { id: string; name: string }[];
  appId: string;
  onAppIdChange: (v: string) => void;
  appSecret: string;
  onAppSecretChange: (v: string) => void;
  channelSupportsGroup: boolean;
  p2pAllowFrom: string;
  onP2pAllowFromChange: (v: string) => void;
  disableP2p: boolean;
  onDisableP2pChange: (v: boolean) => void;
  disableGroup: boolean;
  onDisableGroupChange: (v: boolean) => void;
  groupAllowFrom: string;
  onGroupAllowFromChange: (v: string) => void;
  showToolCalls: boolean;
  onShowToolCallsChange: (v: boolean) => void;
  approvers: string;
  onApproversChange: (v: string) => void;
  selfApprovers: string;
  onSelfApproversChange: (v: string) => void;
  unknownCommandAsAgentInput: boolean;
  onUnknownCommandAsAgentInputChange: (v: boolean) => void;
  callerUserIdField: string;
  onCallerUserIdFieldChange: (v: string) => void;
}

const pickerBoxStyle: React.CSSProperties = {
  maxHeight: 192,
  overflowY: 'auto',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
};
const pickerItemStyle = (selected: boolean): React.CSSProperties => ({
  padding: '10px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid var(--border-color-lighter)',
  background: selected ? 'var(--color-primary-light-9)' : undefined,
  fontWeight: selected ? 'var(--fw-medium)' : undefined,
});
const pickerHintStyle: React.CSSProperties = {
  padding: 12,
  color: 'var(--text-color-placeholder)',
};

function LegacyChannelFields({
  mode,
  bindingMode,
  onBindingModeChange,
  applicationId,
  onApplicationIdChange,
  applications,
  applicationsLoading,
  hasNextPage,
  isFetchingNextPage,
  onApplicationPickerScroll,
  agents,
  appId,
  onAppIdChange,
  appSecret,
  onAppSecretChange,
  channelSupportsGroup,
  p2pAllowFrom,
  onP2pAllowFromChange,
  disableP2p,
  onDisableP2pChange,
  disableGroup,
  onDisableGroupChange,
  groupAllowFrom,
  onGroupAllowFromChange,
  showToolCalls,
  onShowToolCallsChange,
  approvers,
  onApproversChange,
  selfApprovers,
  onSelfApproversChange,
  unknownCommandAsAgentInput,
  onUnknownCommandAsAgentInputChange,
  callerUserIdField,
  onCallerUserIdFieldChange,
}: LegacyChannelFieldsProps) {
  return (
    <>
      <Form.Item label="绑定模式">
        <Select<ChannelAccountBindingMode>
          value={bindingMode}
          onChange={onBindingModeChange}
          options={[
            {
              value: ChannelAccountBindingMode.INSTANCE,
              label: '实例绑定（agent_id 指向应用实例）',
            },
            {
              value: ChannelAccountBindingMode.TEMPLATE,
              label: '模板绑定（agent_id 指向 Agent 模版，用户需 /init）',
            },
          ]}
        />
      </Form.Item>

      {bindingMode === ChannelAccountBindingMode.INSTANCE ? (
        <Form.Item label="智能体实例" required extra="点击选择实例；列表滚动到底自动加载下一页。">
          <div
            role="listbox"
            aria-label="智能体实例"
            style={pickerBoxStyle}
            onScroll={onApplicationPickerScroll}
          >
            {applicationsLoading && applications.length === 0 ? (
              <div style={pickerHintStyle}>加载中…</div>
            ) : applications.length === 0 ? (
              <div style={pickerHintStyle}>暂无应用实例，请先在「应用」中创建。</div>
            ) : (
              <>
                {applications.map((app) => {
                  const label = app.name?.trim() ? `${app.name} (${app.id})` : app.id;
                  const selected = applicationId === app.id;
                  return (
                    <div
                      key={app.id}
                      role="option"
                      aria-selected={selected}
                      title={app.id}
                      onClick={() => onApplicationIdChange(app.id)}
                      style={pickerItemStyle(selected)}
                    >
                      {label}
                    </div>
                  );
                })}
                {isFetchingNextPage && (
                  <div style={{ ...pickerHintStyle, textAlign: 'center', fontSize: 12 }}>
                    加载更多…
                  </div>
                )}
                {!hasNextPage && !isFetchingNextPage && applications.length > 0 && (
                  <div
                    style={{
                      padding: '6px 0',
                      textAlign: 'center',
                      fontSize: 10,
                      color: 'var(--text-color-placeholder)',
                    }}
                  >
                    已加载全部
                  </div>
                )}
              </>
            )}
          </div>
        </Form.Item>
      ) : (
        <Form.Item
          label="Agent 模版"
          required
          extra="选择 Agent 模版，终端用户需执行 /init 创建专属实例。"
        >
          <div role="listbox" aria-label="Agent 模版" style={pickerBoxStyle}>
            {agents.length === 0 ? (
              <div style={pickerHintStyle}>暂无 Agent 模版，请先在「智能体」中创建。</div>
            ) : (
              agents.map((ag) => {
                const label = ag.name?.trim() ? `${ag.name} (${ag.id})` : ag.id;
                const selected = applicationId === ag.id;
                return (
                  <div
                    key={ag.id}
                    role="option"
                    aria-selected={selected}
                    title={ag.id}
                    onClick={() => onApplicationIdChange(ag.id)}
                    style={pickerItemStyle(selected)}
                  >
                    {label}
                  </div>
                );
              })
            )}
          </div>
        </Form.Item>
      )}

      <Form.Item label="应用 ID（凭证）" required>
        <Input
          value={appId}
          onChange={(e) => onAppIdChange(e.target.value)}
          placeholder={
            mode === 'edit' ? '不修改请留空（与应用密钥同时填写才会更新凭证）' : '必填，渠道 App ID'
          }
        />
      </Form.Item>

      <Form.Item label="应用密钥" required>
        <Input.Password
          value={appSecret}
          onChange={(e) => onAppSecretChange(e.target.value)}
          placeholder={mode === 'edit' ? '不修改请留空' : '必填，渠道 App Secret'}
        />
      </Form.Item>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: 12,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border-color-lighter)',
          background: 'var(--fill-color-lighter)',
        }}
      >
        <Typography.Text strong>聊天策略</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6 }}>
          {channelSupportsGroup
            ? '单聊/群聊允许列表为空时不限制来源；填写后仅匹配列表中的发送者用户 ID 或群 ID（与渠道一致）。'
            : '当前渠道类型不支持群聊。单聊允许列表为空时不限制来源；填写后仅匹配列表中的发送者用户 ID。'}
        </Typography.Text>

        <Form.Item label="调用者 ID 字段" style={{ marginBottom: 0 }}>
          <Select
            value={callerUserIdField}
            onChange={onCallerUserIdFieldChange}
            options={[
              { value: '', label: '默认（渠道用户 ID）' },
              { value: 'open_id', label: 'Open ID' },
              { value: 'username', label: '用户名' },
              { value: 'email', label: '邮箱' },
              { value: 'phone', label: '手机号' },
            ]}
          />
        </Form.Item>

        <Checkbox checked={disableP2p} onChange={(e) => onDisableP2pChange(e.target.checked)}>
          禁用单聊
        </Checkbox>

        <Form.Item label="单聊允许的发送者 ID" style={{ marginBottom: 0 }}>
          <Input.TextArea
            value={p2pAllowFrom}
            onChange={(e) => onP2pAllowFromChange(e.target.value)}
            placeholder="每行一个用户 ID，或用逗号分隔"
            rows={3}
          />
        </Form.Item>

        {channelSupportsGroup && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Checkbox
              checked={disableGroup}
              onChange={(e) => onDisableGroupChange(e.target.checked)}
            >
              禁用群聊
            </Checkbox>
            <Form.Item label="群聊允许的群 ID" style={{ marginBottom: 0 }}>
              <Input.TextArea
                value={groupAllowFrom}
                onChange={(e) => onGroupAllowFromChange(e.target.value)}
                placeholder="未勾选「禁用群聊」时生效；空表示允许任意群"
                rows={3}
              />
            </Form.Item>
            <Checkbox
              checked={showToolCalls}
              onChange={(e) => onShowToolCallsChange(e.target.checked)}
            >
              展示工具调用详情
            </Checkbox>
            <Form.Item label="审批人员 ID" style={{ marginBottom: 0 }}>
              <Input.TextArea
                value={approvers}
                onChange={(e) => onApproversChange(e.target.value)}
                placeholder="每行一个审批人员 ID，或用逗号分隔"
                rows={3}
              />
            </Form.Item>
            <Form.Item label="可自我审批人员 ID" style={{ marginBottom: 0 }}>
              <Input.TextArea
                value={selfApprovers}
                onChange={(e) => onSelfApproversChange(e.target.value)}
                placeholder="每行一个可自我审批人员 ID，或用逗号分隔"
                rows={3}
              />
            </Form.Item>
          </Space>
        )}

        <div>
          <Checkbox
            checked={unknownCommandAsAgentInput}
            onChange={(e) => onUnknownCommandAsAgentInputChange(e.target.checked)}
          >
            未命中的斜杠指令透传给 Agent
          </Checkbox>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', fontSize: 12, marginLeft: 24, lineHeight: 1.6 }}
          >
            开启后，未命中系统斜杠指令的消息将作为 Agent 输入运行；关闭则走 /help 兜底（默认）
          </Typography.Text>
        </div>
      </div>
    </>
  );
}
